/**
 * ClickCountsPage — distinct click counting dashboard (feature #167).
 *
 * Behaviour summary:
 *   - Three-bucket ledger: unique / gross / invalid clicks per tenant.
 *   - Top KPI strip shows unique / gross / invalid plus the two derived
 *     rates (unique %, invalid %).
 *   - A time window + scope (offer_id, affiliate_id) filter panel drives
 *     GET /click-counts/summary.
 *   - A breakdown table (per offer + per affiliate) drives
 *     GET /click-counts/breakdown.
 *   - An ad-hoc ingest form below lets operators record a test click so they
 *     can verify the dedupe / bot / malformed paths from the UI.
 *
 * Brand rules: emerald→sky on the h1; zinc borders; rounded-[6/8]px; light
 * theme only; no emojis; no competitor names.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
	clickCountsPaths,
	fetchClickBreakdown,
	fetchClickSummary,
	formatCount,
	formatUnixSecShort,
	ingestClick,
	ingestFormSchema,
	invalidRate,
	scopeLabel,
	uniqueRate,
	type AffiliateBucket,
	type BreakdownResponse,
	type IngestInput,
	type IngestResult,
	type OfferBucket,
	type SummaryResponse,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — consistent with the advertiser-widgets / portal pages.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export interface ClickCountsPageProps {
	/** Deep-link scope from the parent route, e.g. ?offerId=. */
	initialOfferId?: string;
	initialAffiliateId?: string;
}

export function ClickCountsPage(props: ClickCountsPageProps = {}) {
	return (
		<main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 sm:py-14">
			<Breadcrumb />
			<Heading />
			<CountsSection
				initialOfferId={props.initialOfferId}
				initialAffiliateId={props.initialAffiliateId}
			/>
			<DocHint />
		</main>
	);
}

export default ClickCountsPage;

// ---------------------------------------------------------------------------
// Header fragments
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Click counts</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Tracking
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Click counts
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Three buckets: unique (by click id), gross (raw), invalid (bot,
				dedupe, malformed). Filter by offer or affiliate, drill into the
				breakdown, or record a test click to validate the pipeline.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Filters + summary KPI strip
// ---------------------------------------------------------------------------

interface FilterState {
	from: string;
	to: string;
	offerId: string;
	affiliateId: string;
}

const EMPTY_FILTERS: FilterState = {
	from: "",
	to: "",
	offerId: "",
	affiliateId: "",
};

interface CountsState {
	loading: boolean;
	summary: SummaryResponse | null;
	breakdown: BreakdownResponse | null;
	error: string | null;
}

const EMPTY_COUNTS: CountsState = {
	loading: true,
	summary: null,
	breakdown: null,
	error: null,
};

function CountsSection(props: {
	initialOfferId?: string;
	initialAffiliateId?: string;
}) {
	const [filters, setFilters] = useState<FilterState>({
		...EMPTY_FILTERS,
		offerId: props.initialOfferId ?? "",
		affiliateId: props.initialAffiliateId ?? "",
	});
	const [counts, setCounts] = useState<CountsState>(EMPTY_COUNTS);

	const normalizedFilters = useMemo(() => {
		const out: {
			from?: number;
			to?: number;
			offerId?: string;
			affiliateId?: string;
		} = {};
		const fromN = Number(filters.from);
		if (filters.from !== "" && Number.isFinite(fromN)) out.from = fromN;
		const toN = Number(filters.to);
		if (filters.to !== "" && Number.isFinite(toN)) out.to = toN;
		if (filters.offerId.trim()) out.offerId = filters.offerId.trim();
		if (filters.affiliateId.trim())
			out.affiliateId = filters.affiliateId.trim();
		return out;
	}, [filters]);

	const load = useCallback(async () => {
		setCounts((prev) => ({ ...prev, loading: true, error: null }));
		try {
			const [summary, breakdown] = await Promise.all([
				fetchClickSummary(normalizedFilters),
				fetchClickBreakdown({
					from: normalizedFilters.from,
					to: normalizedFilters.to,
				}),
			]);
			setCounts({ loading: false, summary, breakdown, error: null });
		} catch (err) {
			setCounts((prev) => ({
				...prev,
				loading: false,
				error:
					err instanceof Error ? err.message : "Unable to load click counts.",
			}));
		}
	}, [normalizedFilters]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			if (cancelled) return;
			await load();
		})();
		return () => {
			cancelled = true;
		};
	}, [load]);

	const onFilterChange = useCallback(
		(patch: Partial<FilterState>) =>
			setFilters((prev) => ({ ...prev, ...patch })),
		[],
	);

	const reload = useCallback(() => load(), [load]);

	return (
		<section
			aria-label="Click counts"
			className="flex flex-col gap-5"
		>
			<FiltersCard
				filters={filters}
				onChange={onFilterChange}
				onReset={() => setFilters(EMPTY_FILTERS)}
			/>
			<KpiStrip state={counts} />
			<BreakdownCard state={counts} />
			<IngestCard onIngested={reload} />
		</section>
	);
}

function FiltersCard(props: {
	filters: FilterState;
	onChange: (patch: Partial<FilterState>) => void;
	onReset: () => void;
}) {
	return (
		<section
			aria-label="Filters"
			className={`${CARD} flex flex-col gap-4 p-5`}
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-sm font-semibold text-zinc-900">Filters</h2>
				<p className={HINT}>
					UNIX seconds for the time window; leave a field blank to skip it.
				</p>
			</header>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
				<FilterField
					id="cc-from"
					label="From (unix sec)"
					value={props.filters.from}
					onChange={(v) => props.onChange({ from: v })}
					placeholder="1700000000"
				/>
				<FilterField
					id="cc-to"
					label="To (unix sec)"
					value={props.filters.to}
					onChange={(v) => props.onChange({ to: v })}
					placeholder="1800000000"
				/>
				<FilterField
					id="cc-offer"
					label="Offer ID"
					value={props.filters.offerId}
					onChange={(v) => props.onChange({ offerId: v })}
					placeholder="off_123"
				/>
				<FilterField
					id="cc-aff"
					label="Affiliate ID"
					value={props.filters.affiliateId}
					onChange={(v) => props.onChange({ affiliateId: v })}
					placeholder="aff_456"
				/>
			</div>
			<div className="flex items-center justify-end">
				<button
					type="button"
					className={BTN_SECONDARY}
					onClick={props.onReset}
				>
					Reset filters
				</button>
			</div>
		</section>
	);
}

function FilterField(props: {
	id: string;
	label: string;
	value: string;
	onChange: (v: string) => void;
	placeholder?: string;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<label htmlFor={props.id} className={LABEL}>
				{props.label}
			</label>
			<input
				id={props.id}
				type="text"
				value={props.value}
				placeholder={props.placeholder}
				className={INPUT}
				onChange={(e) => props.onChange(e.currentTarget.value)}
			/>
		</div>
	);
}

// ---------------------------------------------------------------------------
// KPI strip
// ---------------------------------------------------------------------------

function KpiStrip(props: { state: CountsState }) {
	const s = props.state.summary;
	const unique = s?.unique ?? 0;
	const gross = s?.gross ?? 0;
	const invalid = s?.invalid ?? 0;
	const byReason = s?.invalid_by_reason ?? {
		bot: 0,
		dedupe: 0,
		malformed: 0,
		other: 0,
	};
	return (
		<section
			aria-label="Click bucket KPIs"
			className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
		>
			<KpiCard
				label="Unique"
				value={formatCount(unique)}
				sub={`${uniqueRate(gross, unique)}% of gross`}
				tone="emerald"
				loading={props.state.loading}
				error={props.state.error}
			/>
			<KpiCard
				label="Gross"
				value={formatCount(gross)}
				sub={`${formatCount(invalid)} invalid (${invalidRate(
					gross,
					invalid,
				)}%)`}
				tone="sky"
				loading={props.state.loading}
				error={props.state.error}
			/>
			<KpiCard
				label="Invalid by reason"
				value={formatCount(invalid)}
				sub={`bot ${byReason.bot} · dedupe ${byReason.dedupe} · malformed ${byReason.malformed} · other ${byReason.other}`}
				tone="zinc"
				loading={props.state.loading}
				error={props.state.error}
			/>
		</section>
	);
}

function KpiCard(props: {
	label: string;
	value: string;
	sub: string;
	tone: "emerald" | "sky" | "zinc";
	loading: boolean;
	error: string | null;
}) {
	const toneClass =
		props.tone === "emerald"
			? "text-emerald-700"
			: props.tone === "sky"
				? "text-sky-700"
				: "text-zinc-700";
	return (
		<article className={`${CARD} flex flex-col gap-2 p-5`}>
			<p className={`text-xs font-semibold uppercase tracking-[0.1em] ${toneClass}`}>
				{props.label}
			</p>
			<p className="text-3xl font-semibold tracking-tight text-zinc-900">
				{props.loading ? "." : props.value}
			</p>
			<p className={HINT}>
				{props.error
					? props.error
					: props.loading
						? "Loading counts."
						: props.sub}
			</p>
		</article>
	);
}

// ---------------------------------------------------------------------------
// Breakdown table
// ---------------------------------------------------------------------------

function BreakdownCard(props: { state: CountsState }) {
	const b = props.state.breakdown;
	const offerRows = b?.by_offer ?? [];
	const affiliateRows = b?.by_affiliate ?? [];
	return (
		<section
			aria-label="Breakdown"
			className="grid grid-cols-1 gap-4 lg:grid-cols-2"
		>
			<BreakdownTable
				title="By offer"
				idKey="offer_id"
				rows={offerRows}
				loading={props.state.loading}
				error={props.state.error}
			/>
			<BreakdownTable
				title="By affiliate"
				idKey="affiliate_id"
				rows={affiliateRows}
				loading={props.state.loading}
				error={props.state.error}
			/>
		</section>
	);
}

function BreakdownTable(props: {
	title: string;
	idKey: "offer_id" | "affiliate_id";
	rows: OfferBucket[] | AffiliateBucket[];
	loading: boolean;
	error: string | null;
}) {
	return (
		<div className={`${CARD} flex flex-col gap-3 p-5`}>
			<header className="flex items-center justify-between gap-2">
				<h3 className="text-sm font-semibold text-zinc-900">{props.title}</h3>
				<p className={HINT}>Sorted by unique desc</p>
			</header>
			{props.error ? (
				<p className={ERROR_TEXT}>{props.error}</p>
			) : props.loading ? (
				<p className={HINT}>Loading breakdown.</p>
			) : props.rows.length === 0 ? (
				<p className={HINT}>No data in the selected window.</p>
			) : (
				<div className="overflow-hidden rounded-[6px] border border-zinc-200">
					<table className="w-full table-fixed text-sm">
						<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
							<tr>
								<th className="truncate px-3 py-2 font-medium">
									{props.idKey === "offer_id" ? "Offer" : "Affiliate"}
								</th>
								<th className="w-20 px-3 py-2 text-right font-medium">
									Unique
								</th>
								<th className="w-20 px-3 py-2 text-right font-medium">
									Gross
								</th>
								<th className="w-20 px-3 py-2 text-right font-medium">
									Invalid
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{props.rows.map((row) => {
								const key =
									props.idKey === "offer_id"
										? (row as OfferBucket).offer_id
										: (row as AffiliateBucket).affiliate_id;
								return (
									<tr key={key}>
										<td className="truncate px-3 py-2 font-mono text-xs text-zinc-800">
											{scopeLabel(key)}
										</td>
										<td className="px-3 py-2 text-right text-zinc-900">
											{formatCount(row.unique)}
										</td>
										<td className="px-3 py-2 text-right text-zinc-900">
											{formatCount(row.gross)}
										</td>
										<td className="px-3 py-2 text-right text-zinc-900">
											{formatCount(row.invalid)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Ingest form — record a test click
// ---------------------------------------------------------------------------

interface IngestFormState {
	clickId: string;
	offerId: string;
	affiliateId: string;
	bot: boolean;
	malformed: boolean;
	occurredAt: string;
	errors: Record<string, string>;
	submitting: boolean;
	serverError: string | null;
	lastResult: IngestResult | null;
}

const EMPTY_INGEST: IngestFormState = {
	clickId: "",
	offerId: "",
	affiliateId: "",
	bot: false,
	malformed: false,
	occurredAt: "",
	errors: {},
	submitting: false,
	serverError: null,
	lastResult: null,
};

function IngestCard(props: { onIngested: () => void }) {
	const [state, setState] = useState<IngestFormState>(EMPTY_INGEST);

	async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = ingestFormSchema.safeParse({
			clickId: state.clickId,
			offerId: state.offerId,
			affiliateId: state.affiliateId,
			bot: state.bot,
			malformed: state.malformed,
			occurredAt: state.occurredAt,
		});
		if (!parsed.success) {
			setState((prev) => ({
				...prev,
				errors: parsed.errors,
				serverError: null,
			}));
			return;
		}
		setState((prev) => ({
			...prev,
			errors: {},
			submitting: true,
			serverError: null,
		}));
		try {
			const result = await ingestClick(parsed.data as IngestInput);
			setState((prev) => ({
				...prev,
				submitting: false,
				lastResult: result,
				clickId: "",
			}));
			props.onIngested();
		} catch (err) {
			setState((prev) => ({
				...prev,
				submitting: false,
				serverError:
					err instanceof Error ? err.message : "Unable to ingest click.",
			}));
		}
	}

	return (
		<section
			aria-label="Record a click"
			className={`${CARD} flex flex-col gap-4 p-5`}
		>
			<header className="flex flex-col gap-1">
				<h2 className="text-sm font-semibold text-zinc-900">
					Record a test click
				</h2>
				<p className={HINT}>
					Send one POST to <span className="font-mono">{clickCountsPaths.base}</span>.
					Re-sending the same click id within a tenant is classified
					<span className="font-mono"> invalid/dedupe</span>.
				</p>
			</header>
			<form onSubmit={onSubmit} className="flex flex-col gap-3">
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
					<div className="flex flex-col gap-1.5">
						<label htmlFor="cc-click-id" className={LABEL}>
							Click ID
						</label>
						<input
							id="cc-click-id"
							type="text"
							className={INPUT}
							value={state.clickId}
							placeholder="click_abc123"
							onChange={(e) =>
								setState((prev) => ({
									...prev,
									clickId: e.currentTarget.value,
									errors: { ...prev.errors, clickId: "" },
								}))
							}
							disabled={state.submitting}
							aria-invalid={Boolean(state.errors.clickId)}
						/>
						{state.errors.clickId && (
							<p className={ERROR_TEXT}>{state.errors.clickId}</p>
						)}
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="cc-ingest-offer" className={LABEL}>
							Offer ID (optional)
						</label>
						<input
							id="cc-ingest-offer"
							type="text"
							className={INPUT}
							value={state.offerId}
							onChange={(e) =>
								setState((prev) => ({
									...prev,
									offerId: e.currentTarget.value,
									errors: { ...prev.errors, offerId: "" },
								}))
							}
							disabled={state.submitting}
						/>
						{state.errors.offerId && (
							<p className={ERROR_TEXT}>{state.errors.offerId}</p>
						)}
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="cc-ingest-aff" className={LABEL}>
							Affiliate ID (optional)
						</label>
						<input
							id="cc-ingest-aff"
							type="text"
							className={INPUT}
							value={state.affiliateId}
							onChange={(e) =>
								setState((prev) => ({
									...prev,
									affiliateId: e.currentTarget.value,
									errors: { ...prev.errors, affiliateId: "" },
								}))
							}
							disabled={state.submitting}
						/>
						{state.errors.affiliateId && (
							<p className={ERROR_TEXT}>{state.errors.affiliateId}</p>
						)}
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="cc-ingest-occurred" className={LABEL}>
							Occurred at (unix sec, optional)
						</label>
						<input
							id="cc-ingest-occurred"
							type="text"
							className={INPUT}
							value={state.occurredAt}
							placeholder="blank = now"
							onChange={(e) =>
								setState((prev) => ({
									...prev,
									occurredAt: e.currentTarget.value,
									errors: { ...prev.errors, occurredAt: "" },
								}))
							}
							disabled={state.submitting}
							aria-invalid={Boolean(state.errors.occurredAt)}
						/>
						{state.errors.occurredAt && (
							<p className={ERROR_TEXT}>{state.errors.occurredAt}</p>
						)}
					</div>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="cc-ingest-flags" className={LABEL}>
							Flags
						</label>
						<div
							id="cc-ingest-flags"
							className="flex flex-wrap items-center gap-3 text-sm text-zinc-700"
						>
							<label className="flex items-center gap-1">
								<input
									type="checkbox"
									checked={state.bot}
									onChange={(e) =>
										setState((prev) => ({
											...prev,
											bot: e.currentTarget.checked,
										}))
									}
									disabled={state.submitting}
								/>
								Bot
							</label>
							<label className="flex items-center gap-1">
								<input
									type="checkbox"
									checked={state.malformed}
									onChange={(e) =>
										setState((prev) => ({
											...prev,
											malformed: e.currentTarget.checked,
										}))
									}
									disabled={state.submitting}
								/>
								Malformed
							</label>
						</div>
					</div>
				</div>
				{state.serverError && (
					<p className={ERROR_TEXT} role="alert">
						{state.serverError}
					</p>
				)}
				{state.lastResult && (
					<p
						className="rounded-[6px] border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
						role="status"
					>
						Recorded <span className="font-mono">{state.lastResult.id}</span> —
						classification{" "}
						<span className="font-mono">{state.lastResult.classification}</span>
						{state.lastResult.reason
							? ` (${state.lastResult.reason})`
							: ""}
						.
					</p>
				)}
				<div className="flex items-center justify-end">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={state.submitting}
					>
						{state.submitting ? "Recording." : "Record click"}
					</button>
				</div>
			</form>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Footer hint
// ---------------------------------------------------------------------------

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How it works</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					<span className="font-medium">Unique</span> is the count of
					DISTINCT click ids within the window (first arrival only).
				</li>
				<li>
					<span className="font-medium">Gross</span> is every ingestion
					attempt. Gross = unique + invalid.
				</li>
				<li>
					<span className="font-medium">Invalid</span> are rejections, broken
					down by bot / dedupe / malformed / other.
				</li>
				<li>
					All filters hit{" "}
					<span className="font-mono">{clickCountsPaths.summary}</span> and{" "}
					<span className="font-mono">{clickCountsPaths.breakdown}</span>,
					tenant-scoped. One tenant's clicks never leak into another's.
				</li>
				<li>
					Last updated:{" "}
					<span className="font-mono">
						{formatUnixSecShort(Math.floor(Date.now() / 1000))}
					</span>
				</li>
			</ul>
		</aside>
	);
}
